import { useSelector } from "react-redux";
import { ACTION_MAP, PRODUCT_SELECTORS, STEP_LEVEL_MAP } from "./mappings";
import { useContext, useEffect } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

// Get current product type from localStorage
export const getCurrentProductType = () => localStorage.getItem("product");

// Universal field updater
export const updateField = (dispatch, field, value) => {
  const productType = getCurrentProductType();
  const actionCreator = ACTION_MAP[productType];

  if (actionCreator) {
    dispatch(actionCreator({ field, value }));
  } else {
    console.warn(`No update action found for product: ${productType}`);
  }
};

export const getMaxLevelForProduct = (productType) => {
  const stepMap = STEP_LEVEL_MAP[productType];
  return stepMap ? Math.max(...Object.values(stepMap)) : 0;
};

export const getStepKeysForProduct = (productType) => {
  const maxLevel = getMaxLevelForProduct(productType);
  return Array.from({ length: maxLevel }, (_, i) => `step${i + 1}`);
};

// Use Redux selector for current product
export const useProductSlice = () => {
  const productType = getCurrentProductType();

  const selector = PRODUCT_SELECTORS[productType] || (() => null);
  return useSelector(selector);
};

// Deep search for a key in nested objects
export const getProductField = (obj, key) => {
  if (!obj || typeof obj !== "object") return undefined;

  if (Object.prototype.hasOwnProperty.call(obj, key)) {
    return obj[key];
  }

  for (const k in obj) {
    if (typeof obj[k] === "object") {
      const found = getProductField(obj[k], key);
      if (found !== undefined) return found;
    }
  }
  return undefined;
};

// Extract multiple keys at once
export const getProductFields = (obj, keys) =>
  keys.reduce((acc, key) => {
    acc[key] = getProductField(obj, key);
    return acc;
  }, {});

export const renameObjectFields = (obj, mapping = {}) => {
  const newObj = {};

  Object.keys(obj).forEach((key) => {
    // get mapped key (could be "", meaning delete)
    const newKey = mapping.hasOwnProperty(key) ? mapping[key] : key;

    // 🚨 skip if mapping explicitly says delete
    if (newKey === "") {
      return;
    }

    if (Array.isArray(obj[key])) {
      // handle arrays recursively
      newObj[newKey] = obj[key].map((item) =>
        typeof item === "object" && item !== null
          ? renameObjectFields(item, mapping)
          : item
      );
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      // handle nested objects
      newObj[newKey] = renameObjectFields(obj[key], mapping);
    } else {
      newObj[newKey] = obj[key];
    }
  });

  return newObj;
};

export function useBlocker(blocker, when = true) {
  const { navigator } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (!when) return;

    const push = navigator.push;
    const replace = navigator.replace;

    navigator.push = (...args) => {
      const result = blocker({ action: "PUSH", args });
      if (result !== false) push(...args);
    };

    navigator.replace = (...args) => {
      const result = blocker({ action: "REPLACE", args });
      if (result !== false) replace(...args);
    };

    return () => {
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [blocker, navigator, when]);
}
